'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import type { NavFeatured } from '@content/navigation/simplified/nav-data';

const Media = styled.span`
  aspect-ratio: 16 / 9;
  background: ${({ theme }) => theme.colors.grey200};
  border-radius: ${({ theme }) => theme.radius.mini};
  display: block;
  overflow: hidden;
  position: relative;
  width: 100%;

  img,
  video {
    block-size: 100%;
    inline-size: 100%;
    inset: 0;
    object-fit: cover;
    position: absolute;
  }

  video {
    opacity: 0;
    transition: opacity var(--nav-row-duration) var(--nav-ease);
  }

  &.isPlaying video {
    opacity: 1;
  }
`;

type FeaturedMediaProps = {
  featured: NavFeatured;
  /** play the preview loop while true; rewind and show the poster otherwise */
  isPlaying: boolean;
};

/**
 * A featured card's media: the poster at rest, and a short muted loop of the
 * preview clip over it while `isPlaying`. The `<video>` mounts only on the
 * first preview, so page loads (and server HTML) carry just the poster.
 * Decorative; the card's link is the interactive element.
 */
const FeaturedMedia = ({ featured, isPlaying }: FeaturedMediaProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hasPreviewed, setHasPreviewed] = useState(false);
  const previewSeconds = featured.previewSeconds ?? 5;

  // Mount the video on the first preview (state adjusted during render).
  if (isPlaying && !hasPreviewed) setHasPreviewed(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.currentTime = 0;
      // play() rejects when the browser blocks it (e.g. data saver); the
      // poster stays
      void video.play().catch(() => {});
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isPlaying, hasPreviewed]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video && video.currentTime >= previewSeconds) {
      video.currentTime = 0;
    }
  }, [previewSeconds]);

  return (
    <Media aria-hidden="true" className={isPlaying ? 'isPlaying' : undefined}>
      {/* remote Wistia delivery; no next/image loader configured for it */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img alt="" loading="lazy" src={featured.poster} />
      {featured.previewSrc && hasPreviewed ? (
        <video
          ref={videoRef}
          muted
          onTimeUpdate={handleTimeUpdate}
          playsInline
          poster={featured.poster}
          preload="none"
          src={featured.previewSrc}
        />
      ) : null}
    </Media>
  );
};

export default FeaturedMedia;
